import { motion } from "framer-motion";
import {
  Bell,
  Building2,
  CalendarCheck,
  Handshake,
  LayoutDashboard,
  LogOut,
  Settings,
  ShieldCheck,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../store/store";
import { logout } from "../../store/reducers/slices/adminSlice";
import { Path } from "../../navigation/routes";

interface NavItem {
  name: string;
  icon: LucideIcon;
  path: string;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const groups: NavGroup[] = [
  {
    label: "Overview",
    items: [
      {
        name: "Dashboard",
        icon: LayoutDashboard,
        path: Path.AdminDashboard,
      },
    ],
  },
  {
    label: "Portfolio",
    items: [
      { name: "Properties", icon: Building2, path: Path.AdminProperty },
      { name: "KYC desk", icon: ShieldCheck, path: Path.AdminKycStatus },
      { name: "Users", icon: Users, path: Path.AdminUsers },
    ],
  },
  {
    label: "Operations",
    items: [
      { name: "Bookings", icon: CalendarCheck, path: "/admin/bookings" },
      { name: "Deal rooms", icon: Handshake, path: Path.AdminDealRoom },
      { name: "Waiting list", icon: Users, path: Path.WaitingList },
    ],
  },
  {
    label: "System",
    items: [
      { name: "Notifications", icon: Bell, path: "/admin/notifications" },
      { name: "Settings", icon: Settings, path: Path.AdminSetting },
    ],
  },
];

function isActivePath(pathname: string, path: string) {
  if (path === Path.AdminDashboard) return pathname === path;
  if (path === Path.AdminDealRoom) {
    return pathname === path || pathname.startsWith(`${path}/`);
  }
  return pathname === path || pathname.startsWith(`${path}/`);
}

export default function Sidebar({ onClose }: { onClose?: () => void }) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const admin = useAppSelector((state) => state.admin.admin);

  const displayName =
    [admin?.first_name, admin?.last_name].filter(Boolean).join(" ") ||
    admin?.name ||
    "Administrator";

  const handleLogout = () => {
    dispatch(logout());
    navigate(Path.AdminLogin, { replace: true });
  };

  return (
    <aside className="flex h-full w-64 flex-col border-r border-zinc-800/80 bg-zinc-950">
      <div className="flex items-start justify-between px-5 pb-5 pt-6">
        <Link to={Path.AdminDashboard} className="min-w-0">
          <p className="text-base font-black tracking-[0.22em] text-white">
            CASAFRIQUE
          </p>
          <p className="mt-1 text-[9px] font-bold uppercase tracking-[0.3em] text-primary">
            Admin console
          </p>
        </Link>

        <button
          type="button"
          onClick={onClose}
          aria-label="Close navigation"
          className="rounded-lg p-1.5 text-zinc-500 hover:bg-zinc-900 hover:text-white md:hidden"
        >
          <X size={18} />
        </button>
      </div>

      <nav className="flex-1 space-y-5 overflow-y-auto px-3 pb-4">
        {groups.map((group) => (
          <div key={group.label}>
            <p className="px-3 pb-2 text-[9px] font-bold uppercase tracking-[0.24em] text-zinc-600">
              {group.label}
            </p>

            <div className="space-y-1">
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = isActivePath(location.pathname, item.path);

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={onClose}
                    className="block"
                  >
                    <motion.div
                      whileHover={{ x: active ? 0 : 3 }}
                      className={`relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-[11px] font-bold uppercase tracking-wider transition ${
                        active
                          ? "bg-primary/10 text-primary"
                          : "text-zinc-500 hover:bg-zinc-900 hover:text-zinc-200"
                      }`}
                    >
                      {active && (
                        <span className="absolute left-0 top-1/2 h-6 w-0.5 -translate-y-1/2 rounded-full bg-primary" />
                      )}

                      <Icon size={16} />
                      {item.name}
                    </motion.div>
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t border-zinc-800/80 p-4">
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-3">
          <p className="truncate text-xs font-bold text-white">{displayName}</p>
          <p className="mt-0.5 truncate text-[10px] text-zinc-500">
            {admin?.email || "admin@casafrique"}
          </p>

          <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-400/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-400">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
            Secure session
          </p>
        </div>

        <button
          type="button"
          onClick={handleLogout}
          className="mt-3 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[11px] font-bold uppercase tracking-wider text-zinc-500 transition hover:bg-red-500/10 hover:text-red-400"
        >
          <LogOut size={15} />
          Sign out
        </button>
      </div>
    </aside>
  );
}
